import { createFeatureSelector, createSelector } from '@ngrx/store'

import * as fromPreferences from './preferences.reducer'
import * as fromPages from './pages.reducer'
import * as fromMenus from './menus.reducer'
import * as fromStatus from './status.reducer'
import * as fromRoot from '../../reducers'

import { createSideMenu } from '../helpers/collapsable';
import { parseEntities } from '../../core/helpers/entities';
import { parseMenuIds } from '../../core/helpers/utility';
import { hasProps } from '../../core/helpers/object'
import { UXPage } from '../../core/models/ux-page';
import chaining from '../../core/helpers/chaining';
import { parseSettings } from '../../core/helpers/settings';
import { ApiSetting } from '../../core/models/setting';

const CATALOG_TYPE = 'catalog'

export interface BaseState {
    status: fromStatus.State,
    pages: fromPages.State,
    menus: fromMenus.State,
    preferences: fromPreferences.State
}

export interface State extends fromRoot.State {
    base: BaseState
}

export const reducers = {
    status: fromStatus.reducer,
    pages: fromPages.reducer,
    menus: fromMenus.reducer,
    preferences: fromPreferences.reducer
}

export const getModuleState = createFeatureSelector<BaseState>('base')

// Pages Selectors

export const getPagesState = createSelector(
    getModuleState,
    (state: BaseState) => state.pages
)
export const isPagesLoading = createSelector(
    getPagesState,
    fromPages.isLoading
)
export const getPages = createSelector(
    getPagesState,
    fromPages.getPages
)
export const getPagesError = createSelector(
    getPagesState,
    fromPages.getError
)
export const isPagesTtlExpired = createSelector(
  getPagesState,
  fromPages.isPagesTtlExpired
)

// Status Selectors
export const getStatusState = createSelector(
    getModuleState,
    (state: BaseState) => state.status
)
export const getRoutePageId = createSelector(
    getStatusState,
    fromStatus.getCurrentId
)
export const getPageTrail = createSelector(
    getStatusState,
    fromStatus.getPageTrail
)
export const getCurrentPageType = createSelector(
  getStatusState,
  fromStatus.getCurrentPageType
)
export const getPageParams = createSelector(
  getStatusState,
  fromStatus.getCurrentPageParams
)
export const isStatusLoading = createSelector(
  getStatusState,
  fromStatus.isStatusLoading
)
export const loadProducts = createSelector(
  getStatusState,
  fromStatus.loadProducts
)
export const showHeader = createSelector(
  getStatusState,
  fromStatus.showHeader
)


// Preference Selectors

export const getPreferenceState = createSelector(
    getModuleState,
    (state: BaseState) => state.preferences
)
export const getViewType = createSelector(
    getPreferenceState,
    fromPreferences.getViewType
)
export const getViewTypeAdjustable = createSelector(
  getPreferenceState,
  fromPreferences.getViewTypeAdjustable
)

export const getTenantSettings = createSelector(
  getPreferenceState,
  state => parseSettings(state && state.tenantSettings)
)

export const getSupportedLanguages = createSelector(
  getPreferenceState,
  state => {
    if (state && state.tenantSettings && state.tenantSettings.length > 0) {
      const setting = state.tenantSettings.find(setting => setting.key === 'supported_languages')

      if (hasProps(setting)) {
        return setting.value as string[]
      }

      // No setting found, return default value
      return ['en', 'nl', 'de']
    }
    // No setting found, return default value
    return ['en', 'nl', 'de']
  }
)

export const getQuantityPicker = createSelector(
  getPreferenceState,
  fromPreferences.getQuantityPicker
)

export const getAppName = createSelector(
  getPreferenceState,
  fromPreferences.getAppName
)

export const getTenantCode = createSelector(
  getPreferenceState,
  fromPreferences.getTenantCode
)

export const getTopMenuStyle = createSelector(
  getPreferenceState,
  fromPreferences.getTopMenuStyle
)

export const getCatalogSortOrders = createSelector(
  getPreferenceState,
  fromPreferences.getCatalogSortOrders,
)

// Complex Selectors
export const getCurrentPage = createSelector(
  getPages,
  getStatusState,
  (pages, status) => pages.find(page => page.id === status.currentId)
)

export const getCatalogPages = createSelector(
    getPages,
    pages =>  {
        let catalogRoot = pages.find(page => page.type === CATALOG_TYPE)
        return (catalogRoot && catalogRoot.children) || []
    }
)
export const isPagesReady = createSelector(
    getPages,
    isPagesLoading,
    (pages, loading) => pages.length > 1 && !loading
)

export const getCatalogPage = createSelector(
  getPages,
  (pages): UXPage =>  {
    return pages.find(page => chaining(page, 'slug') === 'catalog')
  }
)

export const getItemCatalogPage = createSelector(
  getPages,
  (pages): UXPage =>  {
    return pages.find(page => chaining(page, 'slug') === 'item-catalog')
  }
)

export const getHomePage = createSelector(
  getPages,
  (pages): UXPage =>  {
    return pages.find(page => chaining(page, 'slug') === 'home')
  }
)

export const getSearchPage = createSelector(
  getPages,
  (pages): UXPage =>  {
    return pages.find(page => chaining(page, 'slug') === 'search')
  }
)

export const getItemDetailsPage = createSelector(
  getPages,
  (pages): UXPage =>  {
    return pages.find(page => chaining(page, 'slug') === 'item-details')
  }
)

export const getCheckoutPage = createSelector(
  getPages,
  (pages): UXPage =>  {
    return pages.find(page => chaining(page, 'slug') === 'checkout')
  }
)

export const getCatalogPageSettings = createSelector(
  getCatalogPage,
  page => parseSettings(chaining(page, 'settings'))
)

export const getCatalogItemPageSettings = createSelector(
  getItemCatalogPage,
  page => parseSettings(chaining(page, 'settings'))
)

export const getSearchPageSettings = createSelector(
  getSearchPage,
  page => parseSettings(chaining(page, 'settings'))
)

export const getItemDetailsPageSettings = createSelector(
  getItemDetailsPage,
  page => parseSettings(chaining(page, 'settings'))
)

export const getCheckoutPageSettings = createSelector(
  getCheckoutPage,
  page => parseSettings(chaining(page, 'settings'))
)

export const isFullWidthViewEnabled = createSelector(
  getPreferenceState,
  fromPreferences.isFullWidthViewEnabled
)

export const getSideMenu = createSelector(
    getPageTrail,
    createSideMenu
)

// Menus Selectors
export const getMenusState = createSelector(
  getModuleState,
  state => state.menus
)

export const getMenuEntities = createSelector(
  getMenusState,
  fromMenus.getMenuEntities
)

export const getTopMenuList = createSelector(
  getMenusState,
  getMenuEntities,
  (state, entities) => parseEntities(entities)
    .filter(item => ((item.type === 'top') || (item.type === 'both')))
    .sort((a, b) => {
      if (a.sortOrder < b.sortOrder)
        return -1
      if (a.sortOrder > b.sortOrder)
        return 1
      return 0
    })
)

export const getTopMenuIds = createSelector(
  getMenusState,
  getMenuEntities,
  (state, entities) => parseMenuIds(entities, 'top')
)

export const getSideMenuIds = createSelector(
  getMenusState,
  getMenuEntities,
  (state, entities) => parseMenuIds(entities, 'side')
)

export const getSideMenuList = createSelector(
  getMenusState,
  state => parseEntities(state && state.items)
    .filter(item => (item.type === 'side') || (item.type === 'both'))
)

export const getActiveMenuItemId = createSelector(
  getMenusState,
  fromMenus.getActiveMenuItemId
)

export const getActiveMenuChain = createSelector(
  getMenusState,
  fromMenus.getActiveMenuChain
)

export const isMenusTtlExpired = createSelector(
  getMenusState,
  fromMenus.isMenusTtlExpired
)

export const isMegaMenuOpen = createSelector(
  getMenusState,
  fromMenus.isMegaMenuOpen
)
